<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue';

const props = defineProps<{
  formula?: string;
  expression?: string;
  result: string;
  memory?: number;
  error?: string;
  compact?: boolean;
  large?: boolean;
  xlarge?: boolean;
}>();

const resultEl = ref<HTMLElement | null>(null);
let resizeObserver: ResizeObserver | null = null;

function maxResultFontPx(): number {
  if (props.xlarge) return 50;
  if (props.large) return 48;
  if (props.compact) return 36;
  return 42;
}

async function fitResultLine() {
  await nextTick();
  const el = resultEl.value;
  if (!el) return;

  if (props.error) {
    el.style.fontSize = '';
    return;
  }

  const max = maxResultFontPx();
  const min = 16;
  let size = max;
  el.style.fontSize = `${size}px`;
  while (size > min && el.scrollWidth > el.clientWidth) {
    size -= 1;
    el.style.fontSize = `${size}px`;
  }
}

onMounted(() => {
  fitResultLine();
  const container = resultEl.value?.parentElement;
  if (container) {
    resizeObserver = new ResizeObserver(() => fitResultLine());
    resizeObserver.observe(container);
  }
});

onUnmounted(() => resizeObserver?.disconnect());

watch(
  () => [props.result, props.error, props.xlarge, props.large, props.compact],
  fitResultLine,
);
</script>

<template>
  <div class="win-display" :class="{ compact, large, xlarge }" role="region" :aria-label="$t('display.result')">
    <div v-if="memory" class="mem-indicator">M</div>
    <div class="display-inner">
      <div class="expr-line" aria-live="polite">{{ formula || expression || '' }}</div>
      <div ref="resultEl" class="result-line" :class="{ error: error }">
        {{ error ? $t(`errors.${error}`) : result || '0' }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.win-display {
  flex: 0 0 auto;
  padding: 10px 16px 8px;
  text-align: right;
  height: 96px;
  background: var(--win-display-bg);
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.win-display.large {
  height: 128px;
  padding: 12px 16px 10px;
}

.win-display.xlarge {
  height: var(--win-display-h);
  padding: 10px 16px 8px;
  flex: 0 0 var(--win-display-h);
}

.win-display.large .result-line {
  font-size: 48px;
  line-height: 54px;
}

.win-display.xlarge .result-line {
  font-size: 50px;
  line-height: 1.1;
  font-weight: 300;
}

.win-display.xlarge .expr-line {
  font-size: 14px;
  line-height: 20px;
  min-height: 20px;
  max-height: 44px;
  overflow: hidden;
  margin-bottom: 2px;
}

.win-display.compact {
  height: 72px;
}

.mem-indicator {
  position: absolute;
  top: 8px;
  left: 16px;
  font-size: 11px;
  font-weight: 600;
  color: var(--win-text-secondary);
}

.display-inner {
  margin-top: auto;
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.expr-line {
  font-size: 13px;
  line-height: 18px;
  color: var(--win-text-secondary);
  min-height: 18px;
  word-break: break-all;
}

.win-display.large .expr-line {
  font-size: 14px;
  line-height: 20px;
  min-height: 20px;
}

.result-line {
  font-size: 42px;
  font-weight: 300;
  line-height: 1.1;
  color: var(--win-text-display);
  white-space: nowrap;
  overflow: hidden;
  letter-spacing: -0.5px;
}

.result-line.error {
  font-size: 15px;
  line-height: 22px;
  color: #c42b1c;
  font-weight: 400;
}

@media (prefers-color-scheme: dark) {
  .result-line.error {
    color: #ff99a4;
  }
}
</style>
